import type {
  AdminV2LiveAlert,
  AdminV2LiveAlertPriority,
  AdminV2LiveAlertQuickAction,
  AdminV2LiveAlertQuickActionId,
  AdminV2LiveAlertSort,
  AdminV2LiveAlertSource,
  AdminV2LiveAlertStatus,
} from "@/types/adminV2";
import {
  isFormsGlobalContactReviewAlert,
  isFormsSubmissionReviewAlert,
} from "@/vnext/routes/support/live-alerts/liveAlertsFormsReviewModel";
import { isSafeLiveAlertSourceMetadataString } from "@/vnext/routes/support/live-alerts/liveAlertSourceMetadataPolicy";

export {
  isFormsGlobalContactReviewAlert,
  isFormsReviewAlert,
  isFormsSubmissionReviewAlert,
} from "@/vnext/routes/support/live-alerts/liveAlertsFormsReviewModel";
export { LIVE_ALERT_EVENT_TYPE_OPTIONS } from "@/vnext/routes/support/live-alerts/liveAlertEventTypeOptions";

export const LIVE_ALERT_RECORD_SOURCE_OPTIONS: AdminV2LiveAlertSource[] = [
  "support_ticket",
  "issue_report",
  "email_activity",
  "job",
  "automation_run",
  "billing_event",
  "logging_provider_event",
  "google_workspace_gmail",
  "monday_item",
  "manual_alert",
];

export const LIVE_ALERT_SOURCE_OPTIONS: Array<"all" | AdminV2LiveAlertSource> =
  ["all", ...LIVE_ALERT_RECORD_SOURCE_OPTIONS];

export const LIVE_ALERT_PRIORITY_OPTIONS: Array<
  "all" | AdminV2LiveAlertPriority
> = ["all", "critical", "high", "medium", "low"];

export const LIVE_ALERT_STATUS_OPTIONS: Array<"all" | AdminV2LiveAlertStatus> =
  [
    "all",
    "open",
    "in_progress",
    "waiting_on_customer",
    "waiting_on_internal",
    "escalated",
    "resolved",
    "acknowledged",
    "suppressed",
    "closed",
    "archived",
  ];

export const LIVE_ALERT_SORT_OPTIONS: Array<{
  label: string;
  value: AdminV2LiveAlertSort;
}> = [
  { label: "Priority", value: "priority" },
  { label: "Newest update", value: "updatedAt_desc" },
  { label: "Oldest update", value: "updatedAt_asc" },
  { label: "Newest event", value: "occurredAt_desc" },
  { label: "Oldest event", value: "occurredAt_asc" },
  { label: "Event type", value: "eventType_asc" },
  { label: "Admin lead", value: "adminLead_asc" },
  { label: "Status", value: "status_asc" },
  { label: "User", value: "customer_asc" },
  { label: "Organisation", value: "organisation_asc" },
];

export const getLiveAlertSourceLabel = (
  source: AdminV2LiveAlertSource,
): string => {
  switch (source) {
    case "support_ticket":
      return "Support";
    case "issue_report":
      return "Issue report";
    case "email_activity":
      return "Email & Comms";
    case "job":
      return "Job";
    case "automation_run":
      return "Automation";
    case "billing_event":
      return "Stripe/Billing";
    case "logging_provider_event":
      return "Logs";
    case "google_workspace_gmail":
      return "Gmail";
    case "monday_item":
      return "Monday";
    case "manual_alert":
      return "Manual";
  }

  const exhaustiveSource: never = source;
  return exhaustiveSource;
};

export const getLiveAlertPriorityTone = (
  priority: AdminV2LiveAlertPriority,
): "critical" | "high" | "medium" | "low" => priority;

export const getLiveAlertStatusTone = (
  status: AdminV2LiveAlertStatus,
): "open" | "active" | "waiting" | "resolved" | "archived" => {
  if (status === "resolved") {
    return "resolved";
  }

  if (status === "closed" || status === "archived") {
    return "archived";
  }

  if (status === "suppressed") {
    return "waiting";
  }

  if (status === "waiting_on_customer" || status === "waiting_on_internal") {
    return "waiting";
  }

  if (
    status === "in_progress" ||
    status === "escalated" ||
    status === "acknowledged"
  ) {
    return "active";
  }

  return "open";
};

export const getInitials = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "NA";
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  "Account closes in 5 days": "Account closes in 5 days",
  accounting_connection_handoff: "Provider disconnection",
  admin_ops_sop_request: "Admin ops",
  billing_followup: "Billing replies",
  "Codex pull request review": "Code review needs attention",
  disconnect_subscription_handoff: "Billing handoff",
  feature_feedback_capture: "Customer feedback",
  "forms.global_contact.review_required": "Forms contact review",
  "forms.submission.review_required": "Forms submission review",
  issue_intake_triage: "Issue triage",
  manager_approval_case: "Manager approval",
  "Monday bug board exception": "Bug board needs review",
  "Monday cancellation exception": "Cancellation item needs review",
  "Monday improvement exception": "Improvement request needs review",
  "Monday live-alert source unavailable": "Monday alerts need reconnecting",
  "Monday onboarding exception": "Onboarding item needs review",
  "Monday support request exception": "Support request needs review",
  multi_business_sop_review: "Multiple businesses",
  "New Subscription": "New subscription",
  onboarding_stuck_review: "Onboarding check",
  "Pipeline failure": "Pipeline needs attention",
  post_dev_support_comms: "Post-dev support",
  product_direction_rollup: "Product direction",
  provider_sync_triage: "Provider refresh",
  quickbooks_report_variance: "QuickBooks reports",
  "Recovery email not sent": "Recovery email not sent",
  "Renewal payment failed": "Renewal payment failed",
  "Report generation failed": "FHC report failed",
  spoof_qa_handover: "Spoof and QA",
  stripe_lead_automation_review: "Lead routing",
  subscription_required_support_request: "Subscription access",
  support_triage_quality: "Support triage",
  "Trial Not Continued": "Trial not continued",
  "Trial Started": "Trial started",
  ux_polish_review: "UX review",
  walkthrough_booking_followup: "Walkthrough booking",
};

const LIVE_ALERT_ROW_SUBTITLE_MAX_LENGTH = 86;
const LIVE_ALERT_ROW_QUICK_ACTION_LIMIT = 5;
const REVENUE_SOURCE_BLOCKED_REASON =
  "Billing source record is unavailable for this account. Assign this alert to a billing owner or add an internal note for escalation.";
const UNAPPROVED_EXTERNAL_ACTION_BLOCKED_REASON =
  "This workflow needs approved source rules before it can run from Live Alerts.";
const OWNER_HANDOFF_ACTION_BLOCKED_REASON =
  "Open the owner surface to continue. Live Alerts cannot run this action directly.";
const APPROVED_LIVE_ALERT_QUICK_ACTION_IDS =
  new Set<AdminV2LiveAlertQuickActionId>([
    "open_source",
    "view_action_feed",
    "assign_to_me",
    "update_shared_state",
    "add_note",
    "send_internal_comment",
    "suppress",
    "snooze_alert",
    "mark_resolved",
    "archive",
    "reply_support",
    "add_user_note",
    "add_organisation_note",
    "open_crm_contact",
    "edit_crm_status",
    "manage_crm_tags",
    "open_messaging",
    "email_user_draft",
    "open_spoof",
    "open_issue_report",
    "open_feature_request",
    "view_billing_information",
  ]);
interface OwnerHandoffNavigationRule {
  pathPrefixes: readonly string[];
  sourceFamily: string;
}

const OWNER_HANDOFF_NAVIGATION_RULES: Partial<
  Record<AdminV2LiveAlertQuickActionId, OwnerHandoffNavigationRule>
> = {
  // CRITICAL (#5520): approval is limited to the Notes owner surfaces and
  // their navigation contract; server-side permission refusals stay in force.
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/403
  add_user_note: {
    pathPrefixes: ["/crm/contacts", "/users"],
    sourceFamily: "crm",
  },
  add_organisation_note: {
    pathPrefixes: ["/organisations"],
    sourceFamily: "crm",
  },
  manage_crm_tags: {
    pathPrefixes: ["/audiences/tags", "/audiences"],
    sourceFamily: "crm",
  },
  open_messaging: {
    pathPrefixes: ["/email-comms"],
    sourceFamily: "messaging",
  },
  email_user_draft: {
    pathPrefixes: ["/email-comms"],
    sourceFamily: "messaging",
  },
  view_billing_information: {
    pathPrefixes: ["/revenue/billing"],
    sourceFamily: "billing",
  },
};

const getOwnerHandoffNavigationRule = (
  actionId: AdminV2LiveAlertQuickActionId,
): OwnerHandoffNavigationRule | null =>
  OWNER_HANDOFF_NAVIGATION_RULES[actionId] ?? null;

const isPathInsideOwnerSurface = (
  path: string,
  allowedPrefixes: readonly string[],
): boolean =>
  allowedPrefixes.some(
    (prefix) =>
      path === prefix ||
      path.startsWith(`${prefix}/`) ||
      path.startsWith(`${prefix}?`),
  );

const isRevenueSourceAction = (action: AdminV2LiveAlertQuickAction): boolean =>
  action.id === "open_source" &&
  action.destinationPath?.startsWith("/revenue") === true;

const isUnsafeOwnerHandoffAction = (
  action: AdminV2LiveAlertQuickAction,
): boolean => {
  const rule = getOwnerHandoffNavigationRule(action.id);
  if (!rule) {
    return false;
  }

  const contract = action.contract;

  return (
    action.execution !== "navigate" ||
    !action.destinationPath ||
    !isPathInsideOwnerSurface(action.destinationPath, rule.pathPrefixes) ||
    contract?.executionMode !== "navigate" ||
    contract.implementationStatus !== "implemented" ||
    contract.sourceFamily !== rule.sourceFamily ||
    contract.sideEffectType !== "source_navigation"
  );
};

export const getLiveAlertCustomerLabel = (alert: AdminV2LiveAlert): string => {
  const displayName = alert.displayName?.trim();
  if (displayName) {
    return displayName;
  }

  switch (alert.source) {
    case "issue_report":
      return "Internal issue report";
    case "manual_alert":
      if (isFormsSubmissionReviewAlert(alert)) {
        return "Form submitter pending CRM review";
      }
      if (isFormsGlobalContactReviewAlert(alert)) {
        return "Global contact pending CRM review";
      }
      return "Internal manual alert";
    case "job":
      return "Job workflow";
    case "automation_run":
      return "Automation workflow";
    case "billing_event":
      return "Billing workflow";
    case "logging_provider_event":
      return "Provider alert";
    case "google_workspace_gmail":
      return "Gmail sender not matched";
    case "monday_item":
      return "Monday item";
    case "support_ticket":
    case "email_activity":
    default:
      return "Customer not linked";
  }
};

export const getLiveAlertOrganisationLabel = (
  alert: AdminV2LiveAlert,
): string => {
  const organisationName = alert.organisationName?.trim();
  if (organisationName) {
    return organisationName;
  }

  switch (alert.source) {
    case "google_workspace_gmail":
      return "No customer organisation matched";
    case "monday_item":
      return "No customer organisation linked";
    case "issue_report":
    case "manual_alert":
    case "job":
    case "automation_run":
    case "billing_event":
    case "logging_provider_event":
      return "No customer organisation linked";
    case "support_ticket":
    case "email_activity":
    default:
      return "No customer organisation linked";
  }
};

export const formatLiveAlertEventType = (eventType: string): string => {
  const knownLabel = EVENT_TYPE_LABELS[eventType];
  if (knownLabel) {
    return knownLabel;
  }

  const label = eventType
    .replace(/[._-]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();

  return label ? `${label[0].toUpperCase()}${label.slice(1)}` : "Alert";
};

export const getActivityPreview = (alert: AdminV2LiveAlert): string | null =>
  alert.activityPreview[0]?.trim() || null;

export const getLiveAlertSafeMetadataString = (
  alert: AdminV2LiveAlert,
  key: string,
): string | null => {
  const value = alert.safeMetadata[key];
  if (
    typeof value !== "string" ||
    !isSafeLiveAlertSourceMetadataString(key, value)
  ) {
    return null;
  }

  const trimmed = value.replace(/\s+/gu, " ").trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const getLiveAlertStatusDisplayLabel = (
  status: AdminV2LiveAlertStatus,
): string => {
  switch (status) {
    case "unclassified":
      return "Unclassified";
    case "open":
      return "Needs review";
    case "acknowledged":
      return "Pending review";
    case "waiting_on_internal":
      return "Waiting on internal";
    case "waiting_on_customer":
      return "Waiting customer";
    case "in_progress":
      return "In progress";
    case "resolved":
      return "Resolved";
    case "suppressed":
      return "Suppressed";
    case "closed":
      return "Closed";
    case "archived":
      return "Archived";
    case "escalated":
      return "Escalated";
  }
};

/** Summary and page fallback both count lifecycle status, never assignment. */
export const getLiveAlertWaitingOnInternalCount = (
  alerts: ReadonlyArray<Pick<AdminV2LiveAlert, "status">>,
  summaryCount: number | undefined,
): number =>
  summaryCount ??
  alerts.filter((alert) => alert.status === "waiting_on_internal").length;

export const getLiveAlertOwnerRouteBlocked = (
  alert: AdminV2LiveAlert,
): boolean => alert.safeMetadata.ownerRouteBlocked === true;

export const getLiveAlertRowSubtitle = (summary: string): string => {
  const normalized = summary.replace(/\s+/g, " ").trim();
  if (normalized.length <= LIVE_ALERT_ROW_SUBTITLE_MAX_LENGTH) {
    return normalized;
  }

  const sentenceEndIndex = normalized.search(/[.!?](?:\s|$)/);
  if (
    sentenceEndIndex >= 0 &&
    sentenceEndIndex + 1 <= LIVE_ALERT_ROW_SUBTITLE_MAX_LENGTH
  ) {
    return normalized.slice(0, sentenceEndIndex + 1).trim();
  }

  const truncated = normalized.slice(0, LIVE_ALERT_ROW_SUBTITLE_MAX_LENGTH);
  const lastSpaceIndex = truncated.lastIndexOf(" ");
  const safePrefix =
    lastSpaceIndex > 40 ? truncated.slice(0, lastSpaceIndex) : truncated;
  return `${safePrefix.trim()}...`;
};

export const getAvailableRowQuickActions = (
  quickActions: readonly AdminV2LiveAlertQuickAction[] | undefined,
): AdminV2LiveAlertQuickAction[] =>
  (quickActions ?? [])
    .filter(
      (action) =>
        action.available &&
        action.display === "row_and_drawer" &&
        action.id !== "open_source" &&
        action.id !== "view_action_feed",
    )
    .slice(0, LIVE_ALERT_ROW_QUICK_ACTION_LIMIT - 1);

export const getOpenSourceAction = (
  quickActions: readonly AdminV2LiveAlertQuickAction[] | undefined,
): AdminV2LiveAlertQuickAction | null =>
  quickActions?.find((action) => action.id === "open_source") ?? null;

const isJsonRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const metadataStringEquals = (
  metadata: Record<string, unknown>,
  key: string,
  expected: string,
): boolean => {
  const value = metadata[key];
  return typeof value === "string" && value.trim() === expected;
};

export const isAutomationEmailReviewMetadata = (value: unknown): boolean =>
  isJsonRecord(value) &&
  metadataStringEquals(value, "sourceSystem", "automation_centre") &&
  metadataStringEquals(
    value,
    "reviewReason",
    "automation_send_message_review_required",
  );

export const AUTOMATION_EMAIL_REVIEW_SOURCE_BADGE_LABEL = "Automation review";

export const isAudiencesConsumerHandoffMetadata = (value: unknown): boolean =>
  isJsonRecord(value) &&
  metadataStringEquals(value, "sourceSystem", "audiences") &&
  isJsonRecord(value.audiencesHandoff);

export const getLiveAlertDisplaySourceBadgeLabel = (
  alert: AdminV2LiveAlert,
  sourceContextMetadata: unknown = alert.safeMetadata,
): string =>
  isAutomationEmailReviewMetadata(sourceContextMetadata)
    ? AUTOMATION_EMAIL_REVIEW_SOURCE_BADGE_LABEL
    : alert.sourceBadgeLabel;

export const getAudiencesConsumerHandoffOwnerAction = (
  quickActions: readonly AdminV2LiveAlertQuickAction[],
  sourceContextMetadata: unknown,
): AdminV2LiveAlertQuickAction | null => {
  if (!isAudiencesConsumerHandoffMetadata(sourceContextMetadata)) {
    return null;
  }

  return (
    quickActions.find(
      (action) => action.available && action.id === "manage_crm_tags",
    ) ?? null
  );
};

export const getAutomationEmailReviewOwnerAction = (
  quickActions: readonly AdminV2LiveAlertQuickAction[],
  sourceContextMetadata: unknown,
): AdminV2LiveAlertQuickAction | null => {
  if (!isAutomationEmailReviewMetadata(sourceContextMetadata)) {
    return null;
  }

  return (
    quickActions.find(
      (action) => action.available && action.id === "open_messaging",
    ) ??
    quickActions.find(
      (action) => action.available && action.id === "email_user_draft",
    ) ??
    null
  );
};

export const prioritizeLiveAlertQuickActions = (
  quickActions: readonly AdminV2LiveAlertQuickAction[],
  sourceContextMetadata: unknown,
): AdminV2LiveAlertQuickAction[] => {
  const ownerAction = getAutomationEmailReviewOwnerAction(
    quickActions,
    sourceContextMetadata,
  );
  if (!ownerAction) {
    return [...quickActions];
  }

  return [
    ownerAction,
    ...quickActions.filter((action) => action.id !== ownerAction.id),
  ];
};

export const getFirstQuickAction = (
  quickActions: readonly AdminV2LiveAlertQuickAction[],
): AdminV2LiveAlertQuickAction | null =>
  quickActions.length === 0 ? null : quickActions[0];

export const getQuickActionUnavailableMessage = (
  action: AdminV2LiveAlertQuickAction,
): string =>
  action.blockedReason ??
  action.unavailableReason?.message ??
  "Not available for this alert.";

export const getQuickActionUnavailableDetail = (
  action: AdminV2LiveAlertQuickAction,
): string | null =>
  action.unavailableReason?.code !== "unconfigured" &&
  action.unavailableReason?.code !== "invalid_config" &&
  action.blockedReason &&
  action.unavailableReason?.message &&
  action.unavailableReason.message !== action.blockedReason
    ? action.unavailableReason.message
    : null;

const getOwnerSurfaceLabel = (
  action: AdminV2LiveAlertQuickAction,
): string | null => {
  const contract = action.contract;
  const owningSystem =
    typeof contract?.owningSystem === "string"
      ? contract.owningSystem.trim()
      : "";
  const sourceFamily =
    typeof contract?.sourceFamily === "string"
      ? contract.sourceFamily.trim().toLowerCase()
      : "";

  if (sourceFamily === "messaging" || action.id.startsWith("email_")) {
    return "Email & Comms";
  }

  if (
    sourceFamily === "crm" &&
    owningSystem.toLowerCase().includes("audiences")
  ) {
    return "Audiences";
  }

  if (sourceFamily === "crm") {
    return "CRM";
  }

  if (sourceFamily === "automation") {
    return "Automation Centre";
  }

  if (owningSystem && owningSystem.length > 0) {
    return owningSystem;
  }

  return null;
};

export const getQuickActionOwnerHandoffMessage = (
  action: AdminV2LiveAlertQuickAction,
): string | null => {
  if (action.contract?.implementationStatus !== "blocked") {
    return null;
  }

  const owner = getOwnerSurfaceLabel(action);
  if (!owner) {
    return null;
  }

  return `${owner} owns this action; Live Alerts will not run it directly.`;
};

export const isReadOnlyLiveAlertQuickAction = (
  action: AdminV2LiveAlertQuickAction,
): boolean =>
  !action.recordsAction &&
  (action.execution === "navigate" || action.id === "view_action_feed");

export const applyLiveAlertClientActionGuards = (
  action: AdminV2LiveAlertQuickAction,
  args: { revenueMenuVisible: boolean; readOnly?: boolean },
): AdminV2LiveAlertQuickAction => {
  if (args.readOnly && !isReadOnlyLiveAlertQuickAction(action)) {
    return {
      ...action,
      available: false,
      blockedReason:
        "Unclassified alerts are read-only. You can review the source and history.",
    };
  }
  if (
    action.available &&
    !APPROVED_LIVE_ALERT_QUICK_ACTION_IDS.has(action.id)
  ) {
    return {
      ...action,
      available: false,
      blockedReason: UNAPPROVED_EXTERNAL_ACTION_BLOCKED_REASON,
    };
  }

  if (action.available && isUnsafeOwnerHandoffAction(action)) {
    return {
      ...action,
      available: false,
      blockedReason: OWNER_HANDOFF_ACTION_BLOCKED_REASON,
    };
  }

  if (
    action.available &&
    isRevenueSourceAction(action) &&
    !args.revenueMenuVisible
  ) {
    return {
      ...action,
      available: false,
      blockedReason: REVENUE_SOURCE_BLOCKED_REASON,
    };
  }

  return action;
};

export const applyLiveAlertClientActionGuardsToList = (
  quickActions: readonly AdminV2LiveAlertQuickAction[] | undefined,
  args: { revenueMenuVisible: boolean; readOnly?: boolean },
): AdminV2LiveAlertQuickAction[] =>
  (quickActions ?? []).map((action) =>
    applyLiveAlertClientActionGuards(action, args),
  );
